// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductPriceAndActions from "@pace/App/Components/Product/ProductPriceAndActions";

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getIsProductCompareFull } from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductCarouselProductCondensed from "@pace/Overrides/Components/ProductCarouselProductCondensed";
import React, { FC, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";


export interface ProductDetailsCondensedReplacementCardStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
}

export const productDetailsCondensedReplacementCardStyles: ProductDetailsCondensedReplacementCardStyles = {};

const styles = productDetailsCondensedReplacementCardStyles;

const mapStateToProps = (state: ApplicationState, props: WidgetProps & HasProduct) => {
    const replacementProductState = props.product?.detail?.replacementProductId
        ? getProductState(state, props.product.detail.replacementProductId)
        : null;
    return {
        isProductCompareFull: getIsProductCompareFull(state),
        replacementProduct: replacementProductState?.value,
        isLoading: replacementProductState?.isLoading,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
    addProductToCompare,
    removeProductFromCompare,
};

type Props = WidgetProps & HasProduct & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const ProductDetailsCondensedReplacementCard: FC<Props> = ({
    product,
    id,
    replacementProduct,
    isProductCompareFull,
    isLoading,
    addProductToCompare,
    removeProductFromCompare,
    loadProductInfoList,
}) => {
    if (!product?.detail?.replacementProductId) {
        return null;
    }
    const replacementProductId = product.detail.replacementProductId;
    const toasterContext = useContext(ToasterContext);
    useEffect(() => {
        if (replacementProductId && !replacementProduct?.id) {
            loadProductInfoList({
                id,
                getProductCollectionParameter: {
                    productIds: [replacementProductId],
                },
            });
        }
    }, [replacementProductId, id]);

    const onChangeCompare = (value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: product.id });
        } else {
            removeProductFromCompare({ productId: product.id });
        }
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }
    return replacementProduct ? (
        <StyledWrapper {...styles.wrapper} data-test-selector="product-details-replacement-item">
            <Typography {...styles.title}>{translate("Replacement Item")}</Typography>
            <ProductCarouselProductCondensed
                carouselId={id}
                product={replacementProduct}
                showImage
                showBrand
                showPartNumbers
                showPrice={false}
                showAddToCart={false}
                showAddToList={false}
                showTitle
                showTextBadges
                condensedActionsTemplate={() => (
                    <ProductPriceAndActions
                        showPrice
                        showAvailability
                        showAddToCartActions
                        showAddToList
                        maximumNumberOfImageBadges={6}
                        showImageBadges
                        showCompare
                        onChangeCompare={onChangeCompare}
                    />
                )}
            />
        </StyledWrapper>
    ) : null;
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsCondensedReplacementCard)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
